/**
 * Terminal phase (SPEC §2.6). Reached when the host ends early or the room is
 * abandoned; the natural end runs through `superlatives` first.
 *
 * "Play again" is three seconds, not five minutes of re-photographing: same
 * room, same players, same photos, scores reset, used question numbers stay
 * burned.
 */
import { useState } from 'react';
import {
  Button,
  LeaderboardList,
  RankingHistoryList,
  Screen,
  WaitingFor,
} from '../../components/index.js';
import {
  useGameEndedReason,
  useHostControls,
  useIsHost,
  useLeaderboard,
  useRankingHistory,
  useRoomCode,
  useSelfId,
} from '../../store/index.js';
import { useLicensePlayers } from './RoundLeaderboardScreen.js';
import { clearAllRankingDrafts } from './rankingDraft.js';
import { ReviewTabs, type ReviewTab } from './ReviewTabs.js';

const REASON_COPY: Record<'completed' | 'host_ended' | 'abandoned', string> = {
  completed: 'That is the game.',
  host_ended: 'The host called it.',
  abandoned: 'The room went quiet.',
};

export function EndedScreen() {
  const reason = useGameEndedReason();
  const leaderboard = useLeaderboard();
  const players = useLicensePlayers();
  const selfId = useSelfId();
  const isHost = useIsHost();
  const { playAgain } = useHostControls();
  const roomCode = useRoomCode();
  const history = useRankingHistory();
  const [tab, setTab] = useState<ReviewTab>('results');
  const [restarting, setRestarting] = useState(false);

  /**
   * The switch is hidden when there is nothing to review, so the history pane
   * must never be the thing on screen when it is — otherwise a viewer sitting
   * on the history tab when the history goes away (the `welcome` that precedes
   * a reconnect's `superlatives` replay, or "play again") is left looking at
   * "no rankings" with no control to get back to the results.
   */
  const showHistory = tab === 'history' && history.length > 0;

  return (
    <Screen
      eyebrow={<span className="text-green">Game over</span>}
      title={REASON_COPY[reason ?? 'completed']}
      testId="ended-screen"
      bodyClassName="overflow-y-auto"
      footer={
        isHost ? (
          <Button
            full
            data-testid="play-again"
            disabled={restarting}
            onClick={() => {
              setRestarting(true);
              // Scores reset, so no turn's working state should survive.
              clearAllRankingDrafts(roomCode);
              playAgain();
            }}
          >
            Play again
          </Button>
        ) : (
          <p className="text-center text-[0.8125rem] text-ink-soft">The host can go again.</p>
        )
      }
    >
      {history.length > 0 && (
        <ReviewTabs tab={tab} onChange={setTab} historyCount={history.length} />
      )}

      {showHistory ? (
        <RankingHistoryList history={history} players={players} selfId={selfId ?? undefined} />
      ) : leaderboard ? (
        <LeaderboardList
          leaderboard={leaderboard}
          players={players}
          selfId={selfId ?? undefined}
          showRoundPoints={false}
          showScoringHint
        />
      ) : (
        <WaitingFor
          headline="Nothing to add up."
          detail="The game ended before a round finished."
        />
      )}
    </Screen>
  );
}
